import React from 'react';
import { Box, Button, Chip, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DraggableFieldList } from '../../../../components/Common/DraggableFieldList';
import { ContestRichSelect } from '../ContestRichSelect';
import {
    getDurationSelectOptions,
    getFormatSelectOptions,
    getMediaSelectOptions,
    getRatingSelectOptions,
    getVotingSelectOptions,
} from '../contestSelectOptions';
import { CONTEST_API_GENRES, CONTEST_API_TAGS } from '../contestApiMappers';
import type { ContestWizardState } from '../types';
import { editorialHeadlineSx, editorialInputSx, editorialLabelSx, midnight } from '../midnightStyles';

interface StepProps {
    state: ContestWizardState;
    onChange: (patch: Partial<ContestWizardState>) => void;
    onOpenAiAssist?: () => void;
}

export function BriefRequirementsStep({ state, onChange, onOpenAiAssist }: StepProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Creative Brief
            </Typography>
            {onOpenAiAssist && (
                <Typography
                    component="button"
                    type="button"
                    onClick={onOpenAiAssist}
                    sx={{
                        alignSelf: 'flex-start',
                        border: 'none',
                        bgcolor: 'transparent',
                        color: midnight.secondary,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: 12,
                    }}>
                    ✦ Generate requirements with AI
                </Typography>
            )}
            <Box>
                <Typography sx={editorialLabelSx}>Requirements</Typography>
                <DraggableFieldList
                    items={state.requirements}
                    onItemsChange={items => onChange({ requirements: items })}
                    placeholder="Add a requirement…"
                    inputSx={editorialInputSx}
                />
            </Box>
        </Box>
    );
}

export function DiscoverabilityStep({ state, onChange }: StepProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Discoverability
            </Typography>
            <Box>
                <Typography sx={editorialLabelSx}>Tags</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {CONTEST_API_TAGS.map(({ label, value }) => {
                        const active = state.tag.includes(value);
                        return (
                            <Chip
                                key={value}
                                label={label}
                                onClick={() =>
                                    onChange({
                                        tag: active ? state.tag.filter(t => t !== value) : [...state.tag, value],
                                    })
                                }
                                sx={{
                                    bgcolor: active ? midnight.primary : midnight.surfaceHighest,
                                    color: active ? '#392700' : midnight.onSurface,
                                    fontWeight: 600,
                                }}
                            />
                        );
                    })}
                </Box>
            </Box>
            <Box>
                <Typography sx={editorialLabelSx}>Genre</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {CONTEST_API_GENRES.map(({ label, value }) => {
                        const active = state.genre.includes(value);
                        return (
                            <Chip
                                key={value}
                                label={label}
                                onClick={() =>
                                    onChange({
                                        genre: active ? state.genre.filter(x => x !== value) : [...state.genre, value],
                                    })
                                }
                                sx={{
                                    bgcolor: active ? midnight.primary : midnight.surfaceHighest,
                                    color: active ? '#392700' : midnight.onSurface,
                                }}
                            />
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}

export function SubmissionRulesStep({ state, onChange }: StepProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Entry Rules
            </Typography>
            <FormControlLabel
                control={
                    <Switch
                        checked={state.categories_enabled}
                        onChange={e => onChange({ categories_enabled: e.target.checked })}
                    />
                }
                label="Enable submission categories"
                sx={{ color: midnight.onSurface }}
            />
            <FormControlLabel
                control={
                    <Switch checked={state.late_entry} onChange={e => onChange({ late_entry: e.target.checked })} />
                }
                label="Allow late entry (up to 7 days after close)"
                sx={{ color: midnight.onSurface }}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={state.preselection_enabled}
                        onChange={e => onChange({ preselection_enabled: e.target.checked })}
                    />
                }
                label="Enable pre-selection before jury review"
                sx={{ color: midnight.onSurface }}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={state.entry_type === 'single'}
                        onChange={e => onChange({ entry_type: e.target.checked ? 'single' : 'multiple' })}
                    />
                }
                label="Single entry per participant"
                sx={{ color: midnight.onSurface }}
            />
            <Box>
                <Typography sx={{ ...editorialLabelSx, mb: 0.75 }}>Max applications (optional)</Typography>
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    placeholder="Unlimited"
                    value={state.max_participants ?? ''}
                    onChange={e => {
                        const v = e.target.value.trim();
                        onChange({
                            max_participants: v === '' ? null : Math.max(1, parseInt(v, 10) || 1),
                        });
                    }}
                    sx={{
                        ...editorialInputSx,
                        '& .MuiOutlinedInput-root': {
                            bgcolor: midnight.surfaceHighest,
                            borderRadius: '10px',
                        },
                    }}
                />
                <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 12, mt: 0.75 }}>
                    Leave empty for unlimited. When the cap is reached, the public page shows Sold Out.
                </Typography>
            </Box>
            <FormControlLabel
                control={
                    <Switch
                        checked={state.hide_submissions_count}
                        onChange={e => onChange({ hide_submissions_count: e.target.checked })}
                    />
                }
                label="Hide public submission count & entry playlist"
                sx={{ color: midnight.onSurface }}
            />
            <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 12, mt: -2 }}>
                Public pages won’t show how many people applied or the submissions playlist. Capacity still applies;
                Apply stays available until the contest is full.
            </Typography>
        </Box>
    );
}

export function VotingRatingStep({ state, onChange }: StepProps) {
    const publicFlow = state.submission_state === 'public_submission';
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Audience & Voting
            </Typography>
            {!publicFlow && (
                <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 14 }}>
                    Voting is disabled for private submission campaigns.
                </Typography>
            )}
            {publicFlow && (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={state.voting_required}
                                onChange={e => onChange({ voting_required: e.target.checked })}
                            />
                        }
                        label="Enable public voting"
                        sx={{ color: midnight.onSurface }}
                    />
                    <ContestRichSelect
                        label="Voting frequency"
                        value={state.voting_type}
                        options={getVotingSelectOptions()}
                        disabled={!state.voting_required}
                        onChange={v => onChange({ voting_type: v })}
                    />
                </>
            )}
            <ContestRichSelect
                label="Content rating"
                value={state.rating}
                options={getRatingSelectOptions()}
                onChange={v => onChange({ rating: v })}
            />
        </Box>
    );
}

export function TechnicalSpecStep({ state, onChange }: StepProps) {
    const spec = state.technical_spec;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Technical Specifications
            </Typography>
            <ContestRichSelect
                label="Max duration (minutes)"
                value={String(spec.max_duration)}
                options={getDurationSelectOptions()}
                onChange={v =>
                    onChange({
                        technical_spec: { ...spec, max_duration: Number(v) },
                    })
                }
            />
            <ContestRichSelect
                label="Project format"
                value={spec.project_format}
                options={getFormatSelectOptions()}
                onChange={v =>
                    onChange({
                        technical_spec: { ...spec, project_format: v },
                    })
                }
            />
            <ContestRichSelect
                label="Media type"
                value={spec.media_type}
                options={getMediaSelectOptions()}
                onChange={v =>
                    onChange({
                        technical_spec: { ...spec, media_type: v },
                    })
                }
            />
        </Box>
    );
}

export function JudgingFilesStep({ state, onChange, onOpenAiAssist }: StepProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Judging Criteria
            </Typography>

            {onOpenAiAssist && (
                <Box
                    sx={{
                        p: 3,
                        borderRadius: '12px',
                        border: `1px solid ${midnight.outline}`,
                    }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <AutoAwesomeIcon sx={{ color: midnight.secondary }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>AI Assistant</Typography>
                            <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mb: 2 }}>
                                Choose your contest type — we will suggest six tailored criteria for your scorecard.
                                Pick the ones you want, then edit or add more below.
                            </Typography>
                            <Button
                                onClick={onOpenAiAssist}
                                sx={{
                                    color: midnight.secondary,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
                                }}>
                                Generate criteria →
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            <DraggableFieldList
                items={state.score_criteria}
                onItemsChange={items => onChange({ score_criteria: items })}
                placeholder="e.g. Creativity, Technical skill…"
                inputSx={editorialInputSx}
            />
            <Typography sx={editorialLabelSx}>PDF attachments (optional)</Typography>
            <Button
                component="label"
                variant="outlined"
                sx={{ borderColor: midnight.outline, color: midnight.onSurface, alignSelf: 'flex-start' }}>
                Upload PDF
                <input
                    type="file"
                    hidden
                    accept=".pdf"
                    multiple
                    onChange={e => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) onChange({ contest_media_files: files });
                    }}
                />
            </Button>
            {state.contest_media_files.length > 0 && (
                <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant }}>
                    {state.contest_media_files.length} file(s) selected
                </Typography>
            )}
        </Box>
    );
}

export function ReferencesStep({ state, onChange }: StepProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                References & Playlist
            </Typography>
            <TextField
                label="Reference video IDs (PR-…)"
                placeholder="PR-xxxxxxxx"
                value={state.contest_reference.join(', ')}
                onChange={e =>
                    onChange({
                        contest_reference: e.target.value
                            .split(',')
                            .map(s => s.trim())
                            .filter(Boolean),
                    })
                }
                sx={editorialInputSx}
            />
            {state.submission_state === 'public_submission' && (
                <TextField
                    label="Playlist IDs (PL-…)"
                    placeholder="PL-xxxxxxxx"
                    value={state.playlist.join(', ')}
                    onChange={e =>
                        onChange({
                            playlist: e.target.value
                                .split(',')
                                .map(s => s.trim())
                                .filter(Boolean),
                        })
                    }
                    sx={editorialInputSx}
                />
            )}
        </Box>
    );
}

export function ReviewStep({ state }: { state: ContestWizardState }) {
    const placementCount = state.categories_enabled
        ? state.categories.reduce((n, c) => n + (c.prize_structure?.length ?? 0), 0)
        : 0;
    const rows: [string, string][] = [
        ['Submission', state.submission_state === 'public_submission' ? 'Public' : 'Private'],
        ['Title', state.title || '—'],
        ['Categories', state.categories_enabled ? String(state.categories.length) : 'Disabled'],
        ...(state.categories_enabled
            ? [['Prize placements', placementCount ? String(placementCount) : 'None'] as [string, string]]
            : []),
        ['Requirements', String(state.requirements.length)],
        ['Voting', state.voting_required ? state.voting_type : 'Off'],
        ['Rating', state.rating],
    ];
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 32, color: midnight.onSurface }}>
                Review & Submit
            </Typography>
            <Typography sx={{ color: midnight.onSurfaceVariant, mb: 2 }}>
                Next you will lock prize funds from your campaign wallet. Once the pool is fully funded, you can publish
                the campaign.
            </Typography>
            <Box
                sx={{
                    bgcolor: midnight.surfaceLow,
                    borderRadius: '12px',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                }}>
                {rows.map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 14 }}>{k}</Typography>
                        <Typography sx={{ color: midnight.onSurface, fontWeight: 600, fontSize: 14 }}>{v}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
